import { CommonModule } from '@angular/common';
import { Component, ElementRef, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { Functionality, RolesApiService } from '../../services/roles-api.service';
import { notifyResult, reportApiError } from '../../shared/api-result.helper';
import { AppendToBodyDirective } from '../../shared/append-to-body.directive';
import { ConfirmService } from '../../shared/confirm.service';
import { BASE_VIO_TABLE_V2_CONFIG } from '../../shared/vio-table-v2.config';
import { openModalById } from '../../shared/bootstrap-modal.helper';

interface RoleRow {
  _id: string;
  role_name: string;
  functionalities: Functionality[];
  isSystem: boolean;
}

// Mirrors the icons the sidebar (default-sidebar.component.html) uses for each of these same
// pages, so the Add/Edit Role permission picker reads as "the same menu, checkboxes" rather than
// an unrelated plain list. Keyed by lowercased fnname with a generic fallback, since
// allFunctionalities comes from the API and isn't guaranteed to line up 1:1 with the sidebar.
const FUNCTIONALITY_ICONS: Record<string, string> = {
  dashboard: 'pe-7s-rocket',
  'live activity': 'pe-7s-bell',
  reports: 'pe-7s-note2',
  'machine types': 'pe-7s-albums',
  machines: 'pe-7s-tools',
  'products & workflow': 'pe-7s-box2',
  users: 'pe-7s-users',
  operators: 'pe-7s-users',
  'manage roles': 'pe-7s-id',
  settings: 'pe-7s-config',
};
const FUNCTIONALITY_ICON_FALLBACK = 'pe-7s-note';

@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [CommonModule, FormsModule, AppendToBodyDirective, VioTablesV2Module],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
})
export class RolesComponent implements OnInit {
  roles: RoleRow[] = [];
  tableData: any[] = [];
  allFunctionalities: Functionality[] = [];
  loading = true;

  tableConfig: VT2_TABLE_CONFIG = {
    ...BASE_VIO_TABLE_V2_CONFIG,
    tableTitle: 'Roles',
    excelFileName: 'roles',
    excludeFields: ['_id', 'isSystem'],
    columnLabels: { role_name: 'Role Name', functions: 'Functions' },
    actionButtons: [
      {
        icon: '✏️',
        label: 'Edit',
        title: 'Edit role',
        cssClass: 'btn btn-outline-primary btn-sm vio-table-icon-btn vio-icon-edit',
        onClick: (row: any) => {
          const role = this.roles.find((r) => r._id === row._id);
          if (!role) return;
          this.openEditModal(role);
          openModalById('roleModal');
        },
      },
      {
        icon: '🗑️',
        label: 'Delete',
        title: 'Delete role',
        // vio-icon-delete's ::before icon inherits `color` from .btn-outline-danger, so it renders
        // as a proper red Font Awesome trash icon in an outlined box — matching the same delete
        // buttons used elsewhere in the app (e.g. Settings' downtime-reasons list).
        cssClass: 'btn btn-outline-danger btn-sm vio-table-icon-btn vio-icon-delete',
        // deleteRole() already shows the app's own styled confirm dialog (ConfirmService).
        showIf: (row: any) => !row.isSystem,
        onClick: (row: any) => {
          const role = this.roles.find((r) => r._id === row._id);
          if (role) this.deleteRole(role);
        },
      },
    ],
  };

  operation: 'Add' | 'Edit' = 'Add';
  editingId: string | null = null;
  roleNameInput = '';
  selectedFncodes = new Set<string>();

  @ViewChild('roleModalCloseBtn') roleModalCloseBtn!: ElementRef<HTMLButtonElement>;

  private toastr = inject(ToastrService);
  private confirmService = inject(ConfirmService);

  constructor(
    private rolesApi: RolesApiService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    const [rolesRes, fnRes] = await Promise.all([this.rolesApi.list(), this.rolesApi.getFunctionalities()]);
    // Same zone-continuation issue documented in DashboardComponent/SettingsComponent's load() —
    // without this, `loading` flips to false internally but the view stays stuck.
    this.ngZone.run(() => {
      if (rolesRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.roles = rolesRes.Data;
      } else {
        reportApiError(this.toastr, rolesRes);
      }
      if (fnRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.allFunctionalities = fnRes.Data;
      } else {
        reportApiError(this.toastr, fnRes);
      }
      this.tableData = this.roles.map((r) => ({
        _id: r._id,
        role_name: r.role_name,
        functions: this.functionNames(r),
        isSystem: r.isSystem,
      }));
      this.loading = false;
    });
  }

  functionNames(role: RoleRow): string {
    return (role.functionalities || []).map((f) => f.fnname).join(', ') || '—';
  }

  openAddModal() {
    this.operation = 'Add';
    this.editingId = null;
    this.roleNameInput = '';
    this.selectedFncodes = new Set<string>();
  }

  openEditModal(role: RoleRow) {
    this.operation = 'Edit';
    this.editingId = role._id;
    this.roleNameInput = role.role_name;
    this.selectedFncodes = new Set((role.functionalities || []).map((f) => f.fncode));
  }

  isSelected(fncode: string): boolean {
    return this.selectedFncodes.has(fncode);
  }

  iconFor(fnname: string): string {
    return FUNCTIONALITY_ICONS[fnname.trim().toLowerCase()] || FUNCTIONALITY_ICON_FALLBACK;
  }

  toggleFunctionality(fncode: string) {
    if (this.selectedFncodes.has(fncode)) {
      this.selectedFncodes.delete(fncode);
    } else {
      this.selectedFncodes.add(fncode);
    }
  }

  selectAllFunctionalities() {
    this.selectedFncodes = new Set(this.allFunctionalities.map((f) => f.fncode));
  }

  selectNoFunctionalities() {
    this.selectedFncodes = new Set<string>();
  }

  async saveRole() {
    if (!this.roleNameInput.trim()) return;
    const functionalities = this.allFunctionalities.filter((f) => this.selectedFncodes.has(f.fncode));
    const payload = { role_name: this.roleNameInput.trim(), functionalities };

    const res =
      this.operation === 'Add' ? await this.rolesApi.create(payload) : await this.rolesApi.update(this.editingId!, payload);
    if (!notifyResult(this.toastr, res, `Role ${this.operation === 'Add' ? 'created' : 'updated'}.`)) return;

    this.roleModalCloseBtn.nativeElement.click();
    await this.load();
  }

  async deleteRole(role: RoleRow) {
    if (role.isSystem) return;
    const ok = await this.confirmService.confirm({
      message: `Delete role "${role.role_name}"? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    const res = await this.rolesApi.delete(role._id);
    if (!notifyResult(this.toastr, res, `${role.role_name} deleted.`)) return;
    await this.load();
  }
}
